import { settingsApi, type ProjectSettingEntity, type UpdateSettingRequest, type WeekHours, type DayHours } from '../../api/SettingsApi';
import { filesApi } from '../../api/FilesApi';
import type { PendingFile } from '../../components/forms/ImageUpload';

function normalizeWeek(raw: unknown): WeekHours {
  if (!raw || typeof raw !== 'object') return {};
  const result: WeekHours = {};
  const obj = raw as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const day = obj[key] as Partial<DayHours> | null;
    if (!day || typeof day !== 'object') continue;
    result[Number(key)] = {
      isOpen: !!day.isOpen,
      time: Array.isArray(day.time)
        ? day.time.map((t: any) => ({
            openTime: t?.openTime ?? '',
            closeTime: t?.closeTime ?? '',
          }))
        : [],
    };
  }
  return result;
}

export type SettingRecord = {
  projectSettingId: string;
  coverImage: string;
  logoImage: string;
  coverImageUrl: string;
  logoImageUrl: string;
  motto: string;
  welcomeMessage: string;
  address: string;
  addressLink: string;
  contactNo: string;
  contactEmail: string;
  themeColor: string;
  buttonColor: string;
  serviceColor: string;
  offsiteName: string;
  externalName: string;
  remarkLabel: string;
  defaultLanguage: string;
  allowOffsiteBooking: boolean;
  allowExternalBooking: boolean;
  allowCustomerLogin: boolean;
  productSectionEnabled: boolean;
  commissionPercentage: string;
  offsiteInterval: string;
  onsiteInterval: string;
  offsiteHours: WeekHours;
  onsiteHours: WeekHours;
  invoiceNotes: string;
};

function resolveImageUrl(
  files: { s3Url: string }[] | null | undefined,
): string {
  if (!files) return '';
  if (Array.isArray(files) && files.length > 0) return files[0].s3Url ?? '';
  if (!Array.isArray(files) && (files as { s3Url: string })?.s3Url) return (files as { s3Url: string }).s3Url;
  return '';
}

class SettingsStore {
  private toRecord(entity: ProjectSettingEntity): SettingRecord {
    return {
      projectSettingId: entity.projectSettingId ?? '',
      coverImage: entity.coverImage ?? '',
      logoImage: entity.logoImage ?? '',
      coverImageUrl: resolveImageUrl(entity.coverImageFiles),
      logoImageUrl: resolveImageUrl(entity.logoImageFiles),
      motto: entity.motto ?? '',
      welcomeMessage: entity.welcomeMessage ?? '',
      address: entity.address ?? '',
      addressLink: entity.addressLink ?? '',
      contactNo: entity.contactNo ?? '',
      contactEmail: entity.contactEmail ?? '',
      themeColor: entity.themeColor ?? '#dc2626',
      buttonColor: entity.buttonColor ?? '#dc2626',
      serviceColor: entity.serviceColor ?? '#dc2626',
      offsiteName: entity.offsiteName ?? '',
      externalName: entity.externalName ?? '',
      remarkLabel: entity.remarkLabel ?? '',
      // Empty means 'follow the visitor', which is what every project did before this setting existed.
      defaultLanguage: entity.defaultLanguage ?? '',
      allowOffsiteBooking: entity.allowOffsiteBooking ?? true,
      allowExternalBooking: entity.allowExternalBooking ?? false,
      allowCustomerLogin: entity.allowCustomerLogin ?? false,
      // Absent on records saved before this setting existed, which should keep showing products.
      productSectionEnabled: entity.productSectionEnabled ?? true,
      commissionPercentage: entity.commissionPercentage ?? '',
      offsiteInterval: entity.operation?.offsite?.interval ?? '',
      onsiteInterval: entity.operation?.onsite?.interval ?? '',
      offsiteHours: normalizeWeek(entity.operation?.offsite?.hours),
      onsiteHours: normalizeWeek(entity.operation?.onsite?.hours),
      invoiceNotes: entity.metadata?.invoiceNotes ?? '',
    };
  }

  private toPayload(record: SettingRecord): UpdateSettingRequest {
    return {
      coverImage: record.coverImage || undefined,
      logoImage: record.logoImage || undefined,
      motto: record.motto,
      welcomeMessage: record.welcomeMessage,
      address: record.address,
      addressLink: record.addressLink,
      contactNo: record.contactNo,
      contactEmail: record.contactEmail,
      themeColor: record.themeColor,
      buttonColor: record.buttonColor,
      serviceColor: record.serviceColor,
      offsiteName: record.offsiteName,
      externalName: record.externalName,
      remarkLabel: record.remarkLabel,
      defaultLanguage: record.defaultLanguage,
      allowOffsiteBooking: record.allowOffsiteBooking,
      allowExternalBooking: record.allowExternalBooking,
      allowCustomerLogin: record.allowCustomerLogin,
      productSectionEnabled: record.productSectionEnabled,
      commissionPercentage: record.commissionPercentage,
      operation: {
        offsite: { interval: record.offsiteInterval, hours: record.offsiteHours },
        onsite: { interval: record.onsiteInterval, hours: record.onsiteHours },
      },
      metadata: {
        invoiceNotes: record.invoiceNotes,
      },
    };
  }

  async fetch(): Promise<SettingRecord> {
    const { data } = await settingsApi.get();
    return this.toRecord(data);
  }

  async uploadFile(pending: PendingFile): Promise<string> {
    const { data } = await filesApi.upload(pending.name, pending.base64);
    return data.fileId;
  }

  async save(
    record: SettingRecord,
    pendingCover: PendingFile | null,
    pendingLogo: PendingFile | null,
  ): Promise<SettingRecord> {
    const updated = { ...record };

    if (pendingCover) {
      updated.coverImage = await this.uploadFile(pendingCover);
    }
    if (pendingLogo) {
      updated.logoImage = await this.uploadFile(pendingLogo);
    }

    const { data } = await settingsApi.update(this.toPayload(updated));
    return this.toRecord(data);
  }
}

export const settingsStore = new SettingsStore();
